import React from "react";
import {
  IActionsList,
  EActionTypes,
  TActionItem,
  TActionsData,
} from "./IActionsList";
import { classNames } from "@features/utils/reactFunctions";
import * as elements from "@appStyles/_elements.module.scss";
import { Link } from "react-router-dom";
import {
  COMING_SOON_BUY_CURRENCIES,
  COMING_SOON_SELL_CURRENCIES,
} from "@funds/consts";
import { MAIN_CURRENCY, MAIN_CURRENCY_LABEL } from "@bundles/consts";
import { renameMainCurrency } from "@features/utils/functions";
import CustodyOfFundsNotice from "../CustodyOfFundsNotice/CustodyOfFundsNotice";

import cartPlusSVG from "./images/cart-plus.svg?url";
import cartMinusSVG from "./images/cart-minus.svg?url";
import moneyCheckSVG from "./images/money-check.svg?url";
import coinsSVG from "./images/coins.svg?url";

import * as styles from "./ActionsListStyles.module.scss";

const actionsData: TActionsData = {
  [EActionTypes.buy]: {
    icon: cartPlusSVG,
    type: EActionTypes.buy,
    label: "Buy",
    description: (currency: string, isComingSoon: boolean) => {
      if (isComingSoon) {
        return "This feature is coming soon. It will allow you to buy crypto using your USDT balance.";
      }

      if (currency === "bft") {
        return (
          "Buy BFT and become a BF Premium or Premium+ member. Just click the " +
          "below button to know more and to make a purchase."
        );
      }

      if (currency === MAIN_CURRENCY) {
        return `Buy ${MAIN_CURRENCY_LABEL.toUpperCase()}
          or any of available USD stablecoins by clicking the "Buy Now" button`;
      }

      return `Buy ${currency.toUpperCase()}, just click the below button to know more and to make a purchase.`;
    },
    link: (isComingSoon: boolean) => (isComingSoon ? "Coming Soon" : "Buy Now"),
  },
  [EActionTypes.sell]: {
    icon: cartMinusSVG,
    type: EActionTypes.sell,
    label: "Sell",
    description: (currency: string) => {
      return process.env.ENABLE_BINANCE_TRADING
        ? `Sell ${renameMainCurrency(currency).toUpperCase()},
          just click the below button to know more and to make a purchase.`
        : "This feature is coming soon. It will allow you to sell crypto using your USDT balance.";
    },
    link: (isComingSoon: boolean) =>
      isComingSoon ? "Coming Soon" : "Sell Now",
  },
  [EActionTypes.deposit]: {
    icon: moneyCheckSVG,
    type: EActionTypes.deposit,
    label: "Deposit",
    description: (currency: string) => {
      if (currency === "bft") {
        return (
          "Deposit BFT into your BF account by clicking the “Deposit Now” button" +
          " below and become a BF Premium or Premium+ member."
        );
      }

      if (currency === MAIN_CURRENCY) {
        return (
          "Deposit USD, any other fiat currency or USDT (Stablecoin) into your BF " +
          "account by clicking the “Deposit Now” button below"
        );
      }

      return `Deposit ${currency.toUpperCase()}, into your
        BF account by clicking the “Deposit Now” button below`;
    },
    link: () => "Deposit Now",
  },
  [EActionTypes.withdraw]: {
    icon: coinsSVG,
    type: EActionTypes.withdraw,
    label: "Withdraw",
    description: (currency: string) => {
      if (currency === MAIN_CURRENCY) {
        return (
          "Withdraw your USDT balance as USD or any of the available USD " +
          "stablecoins by clicking the “Withdraw now” button below"
        );
      }

      return `Withdraw your ${currency.toUpperCase()} by clicking the “Withdraw now” button below`;
    },
    link: () => "Withdraw Now",
  },
};

export const ActionsList = (props: IActionsList) => {
  const actionsToBeRendered = [...props.availableActions];

  if (!process.env.ENABLE_BINANCE_TRADING) {
    if (COMING_SOON_SELL_CURRENCIES.includes(props.currency)) {
      actionsToBeRendered.push(EActionTypes.sell);
    }

    if (COMING_SOON_BUY_CURRENCIES.includes(props.currency)) {
      actionsToBeRendered.push(EActionTypes.buy);
    }
  }

  const checkComingSoon = (actionBox: TActionItem) => {
    if (actionBox.type === EActionTypes.sell) {
      return COMING_SOON_SELL_CURRENCIES.includes(props.currency);
    }

    if (actionBox.type === EActionTypes.buy) {
      return COMING_SOON_BUY_CURRENCIES.includes(props.currency);
    }

    return false;
  };

  const renderItem = (actionBox: TActionItem): JSX.Element => {
    const buyOrSellSoon =
      process.env.ENABLE_BINANCE_TRADING &&
      !process.env.ENABLE_LOCK_ON_BINANCE_TRADING
        ? false
        : checkComingSoon(actionBox);

    return (
      <li
        key={actionBox.label}
        className={classNames(
          styles.actionItem,
          buyOrSellSoon && styles.actionItemOpacity,
        )}
      >
        <div className={styles.actionContent}>
          <img className={styles.actionIcon} src={actionBox.icon} />
          <p className={styles.actionLabel}>{actionBox.label}</p>
          <p className={styles.actionDescription}>
            {actionBox.description(props.currency, buyOrSellSoon)}
          </p>
          <Link
            to={`manage/${actionBox.type}?currency=${props.currency}`}
            className={classNames(
              elements.btn,
              elements.btnSecondary,
              styles.actionButton,
              buyOrSellSoon && styles.actionButtonComingSoon,
            )}
          >
            {actionBox.link(buyOrSellSoon)}
          </Link>
        </div>
      </li>
    );
  };

  return (
    <>
      <CustodyOfFundsNotice
        noDivider={true}
        style={{ margin: "0 auto 20px auto" }}
      />

      <ul className={styles.actionList}>
        {actionsToBeRendered.map((action) => {
          return renderItem(actionsData[action]);
        })}
        {props.availableActions.length === 0 && (
          <p>
            None of the actions are currently available to you for this
            currency.
          </p>
        )}
      </ul>
    </>
  );
};

export default ActionsList;
